<template>
  <div>
    <v-progress-linear v-if="loadingDeposito" indeterminate class="mb-2" />
    <v-alert v-else-if="!depositoIdSeleccionado" type="warning" dense class="mb-3">
      Seleccioná un depósito en la configuración del usuario para ver las órdenes de compra.
    </v-alert>
    <v-row v-else no-gutters>
      <v-col cols="12" md="3" lg="2">
        <orden-compra-filters
          @updateFilters="setFilters"
          @clearFilter="clearFilters"
        />
      </v-col>
      <v-col cols="12" md="9" lg="10" class="pl-md-3">
    <div class="table-fixed-header-wrap">
    <v-data-table
      :headers="headers"
      fixed-header
      height="calc(100vh - 260px)"
      :items="items"
      :server-items-length="totalItems"
      :loading="loading"
      :options.sync="options"
      :footer-props="{ itemsPerPageOptions: [5, 10, 25, 50] }"
      @update:options="fetch"
    >
      <template v-slot:item.numeroOrdenCompra="{ item }">
        <span class="font-weight-medium">{{ textoNumeroOrdenCompra(item) }}</span>
      </template>
      <template v-slot:item.proveedor="{ item }">
        {{ item.proveedor ? item.proveedor.nombre : "—" }}
      </template>
      <template v-slot:item.deposito="{ item }">
        {{ item.deposito ? item.deposito.nombre : "—" }}
      </template>
      <template v-slot:item.precioTotal="{ item }">
        ${{ Number(item.precioTotal || 0).toFixed(2) }}
      </template>
      <template v-slot:item.createdAt="{ item }">
        {{ formatFecha(item.createdAt) }}
      </template>
      <template v-slot:item.estadoOrdenCompra="{ item }">
        <v-chip small dark class="white--text" :color="chipEstadoOrdenColor(item)">
          {{ labelEstadoOrden(item) }}
        </v-chip>
      </template>
      <template v-slot:item.estadoPago="{ item }">
        <v-chip small dark class="white--text" :color="chipEstadoPagoColor(item)">
          {{ labelEstadoPagoLista(item) }}
        </v-chip>
      </template>
      <template v-slot:item.fechaPagoCompletado="{ item }">
        {{ item.fechaPagoCompletado ? formatFecha(item.fechaPagoCompletado) : "—" }}
      </template>
      <template v-slot:item.actions="{ item }">
        <div class="table-actions-cell">
          <show-button icon="mdi-eye" @click="$emit('show', item)" />
          <edit-button v-if="ordenEditable(item)" @click="$emit('edit', item)" />
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on" @click="descargarPDF(item)">
                <v-icon small color="red">mdi-file-pdf-box</v-icon>
              </v-btn>
            </template>
            <span>Descargar PDF</span>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>
    </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { EditButton, ShowButton } from "@dracul/common-frontend";
import OrdenCompraProvider from "../../providers/OrdenCompraProvider";
import UserConfigurationProvider from "../../../user-configuration/providers/UserConfigurationProvider";
import { formatFechaVentas } from "../../../ventas/utils/dateFormat";
import OrdenCompraFilters from "./OrdenCompraFilters/OrdenCompraFilters.vue";

export default {
  name: "OrdenCompraList",
  components: { EditButton, ShowButton, OrdenCompraFilters },
  data() {
    return {
      items: [],
      totalItems: 0,
      loading: false,
      loadingDeposito: false,
      downloadingId: null,
      depositoIdSeleccionado: null,
      activeFilters: {
        estadoOrdenCompra: null,
        estadoPago: null,
        proveedorNombre: null,
        fechaDesde: null,
        fechaHasta: null,
        fechaPagoCompletadoDesde: null,
        fechaPagoCompletadoHasta: null,
        precioTotalMin: null,
        precioTotalMax: null,
        descuentoMin: null,
        recargoMin: null,
      },
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["createdAt"],
        sortDesc: [true],
      },
    };
  },
  mounted() {
    this.loadDepositoSeleccionado();
  },
  activated() {
    this.loadDepositoSeleccionado();
  },
  computed: {
    headers() {
      return [
        { text: "Nº OC", value: "numeroOrdenCompra", width: 96, sortable: true },
        { text: "Proveedor", value: "proveedor" },
        { text: "Depósito", value: "deposito" },
        { text: "Total", value: "precioTotal" },
        { text: "Estado", value: "estadoOrdenCompra", sortable: false },
        { text: "Pago", value: "estadoPago" },
        { text: "Fecha de creación", value: "createdAt" },
        { text: "Fecha pago total", value: "fechaPagoCompletado", sortable: false },
        {
          text: "Acciones",
          value: "actions",
          sortable: false,
          width: 140,
          cellClass: "cell-actions",
          class: "cell-actions",
        },
      ];
    },
  },
  methods: {
    textoNumeroOrdenCompra(item) {
      if (!item) return "—";
      const n = item.numeroOrdenCompra;
      return n != null && n !== "" ? String(n) : "—";
    },
    labelEstadoPagoLista(item) {
      if (!item) return "—";
      const m = { PAGADO: "Pagada", PAGO_PARCIAL: "Pago parcial", NO_PAGADO: "Impaga" };
      return m[item.estadoPago] || item.estadoPago || "—";
    },
    chipEstadoPagoColor(item) {
      if (!item) return "grey";
      if (item.estadoPago === "PAGADO") return "success";
      if (item.estadoPago === "PAGO_PARCIAL") return "amber darken-2";
      return "error";
    },
    labelEstadoOrden(item) {
      if (!item) return "—";
      const m = {
        BORRADOR: "Borrador",
        CONFIRMADA: "Confirmada",
        RECIBIDA: "Recibida",
        CANCELADA: "Cancelada",
      };
      return m[item.estadoOrdenCompra] || item.estadoOrdenCompra || "—";
    },
    chipEstadoOrdenColor(item) {
      if (!item) return "grey";
      if (item.estadoOrdenCompra === "BORRADOR") return "grey darken-1";
      if (item.estadoOrdenCompra === "CONFIRMADA") return "primary";
      if (item.estadoOrdenCompra === "RECIBIDA") return "success";
      if (item.estadoOrdenCompra === "CANCELADA") return "red darken-2";
      return "grey";
    },
    ordenEditable(item) {
      return !!(item && item.puedeEditarContenido);
    },
    formatFecha(val) {
      return formatFechaVentas(val);
    },
    nombreArchivo(item) {
      const num = item.numeroOrdenCompra != null ? item.numeroOrdenCompra : "";
      const prov =
        item.proveedor && item.proveedor.nombre
          ? String(item.proveedor.nombre)
              .replace(/[/\\:*?"<>|]/g, "")
              .trim()
              .slice(0, 40)
          : "proveedor";
      const fechaStr = item.createdAt
        ? formatFechaVentas(item.createdAt).slice(0, 10)
        : new Date().toISOString().slice(0, 10);
      return `OrdenCompra_${num ? `N${num}_` : ""}${prov}_${fechaStr}.pdf`;
    },
    async descargarPDF(item) {
      const id = item.id || item._id;
      if (!id || this.downloadingId) return;
      this.downloadingId = id;
      try {
        const r = await OrdenCompraProvider.generarPDFBase64(id);
        const base64 = r.data && r.data.ordenCompraGenerarPDFBase64;
        if (!base64) return;
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const blob = new Blob([new Uint8Array(byteNumbers)], { type: "application/pdf" });
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = this.nombreArchivo(item);
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      } catch (e) {
        console.error(e);
      } finally {
        this.downloadingId = null;
      }
    },
    async loadDepositoSeleccionado() {
      this.loadingDeposito = true;
      try {
        let depositoId = UserConfigurationProvider.getDepositoIdFromCache();
        if (!depositoId) {
          const r = await UserConfigurationProvider.getUserConfiguration(true);
          const config = r.data && r.data.userConfigurationGet;
          depositoId =
            (config && config.depositoIdSeleccionado) ||
            (config && config.deposito && config.deposito.id) ||
            null;
        }
        this.depositoIdSeleccionado = depositoId;
        if (depositoId) this.fetch();
        else {
          this.items = [];
          this.totalItems = 0;
        }
      } catch (e) {
        console.error(e);
        this.depositoIdSeleccionado = null;
        this.items = [];
        this.totalItems = 0;
      } finally {
        this.loadingDeposito = false;
      }
    },
    setFilters(filters) {
      this.options.page = 1;
      this.activeFilters = { ...filters };
      this.fetch();
    },
    clearFilters() {
      this.options.page = 1;
      this.activeFilters = {
        estadoOrdenCompra: null,
        estadoPago: null,
        proveedorNombre: null,
        fechaDesde: null,
        fechaHasta: null,
        fechaPagoCompletadoDesde: null,
        fechaPagoCompletadoHasta: null,
        precioTotalMin: null,
        precioTotalMax: null,
        descuentoMin: null,
        recargoMin: null,
      };
      this.fetch();
    },
    fetch() {
      if (!this.depositoIdSeleccionado) {
        this.items = [];
        this.totalItems = 0;
        return;
      }
      this.loading = true;
      const { page, itemsPerPage, sortBy, sortDesc } = this.options;
      const orderBy = sortBy && sortBy[0] ? sortBy[0] : "createdAt";
      const orderDesc = sortDesc && sortDesc[0] !== undefined ? sortDesc[0] : true;
      const filters = { depositoId: this.depositoIdSeleccionado, ...this.activeFilters };
      OrdenCompraProvider.paginateOrdenesCompra(page, itemsPerPage, null, orderBy, orderDesc, filters)
        .then((r) => {
          const p = r.data && r.data.ordenCompraPaginate;
          this.items = (p && p.items) || [];
          this.totalItems = (p && p.totalItems) || 0;
        })
        .catch((err) => console.error(err))
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.table-fixed-header-wrap {
  min-height: 0;
}
</style>
